import { NgFor, NgIf, NgStyle, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';

import {
  ButtonDirective,
  ButtonIconDirective,
  ButtonTextDirective,
  DynamicModalComponent,
  ImageSizes,
  SvgComponent,
} from '@app/base-components';
import { DlContentClickService } from 'haba-base-components';
import { I18nModule, Product, ProductScope, ProductService } from 'haba-core';
import { ProductHit } from 'haba-emporix';
import { ImageComponent } from '../../../product/product-card-base/image/image.component';
import { HabaQaId, TextQaId } from '@app/helpers';
import { ProductAddToWishlistComponent } from 'src/app/wishlist/@shared/product-add-to-wishlist/product-add-to-wishlist.component';
import { StorefrontSubdomain } from 'src/app/@enums/storefrontSubdomain';
import { ProductStockModalComponent } from '@app/common';
import { BREAKPOINT, BreakpointService, ModalService } from 'haba-storefrontlib';
import { distinctUntilChanged, filter, Subject, take, takeUntil } from 'rxjs';
import { FormControl } from '@angular/forms';
import { AddToCartPopupComponent, HabaAddToCartComponent } from '@app/cart';
import { ConfigurationOverlayComponent } from 'src/app/configurator/configuration-overlay/configuration-overlay.component';
import { ProductPriceDisplayComponent } from '../../../product/product-price-display/product-price-display.component';
import { Router, RouterLink, RouterModule } from '@angular/router';

@Component({
  selector: 'app-product-advisor-card',
  templateUrl: './product-advisor-card.component.html',
  styleUrls: ['./product-advisor-card.component.scss'],
  imports: [
    RouterModule,
    NgIf,
    SvgComponent,
    NgFor,
    NgStyle,
    NgTemplateOutlet,
    I18nModule,
    ImageComponent,
    ProductAddToWishlistComponent,
    HabaAddToCartComponent,
    AddToCartPopupComponent,
    ConfigurationOverlayComponent,
    ProductPriceDisplayComponent,
    ButtonDirective,
    ButtonIconDirective,
    ButtonTextDirective
],
})
export class ProductAdvisorCardComponent implements OnInit {
  @Input() product: ProductHit;

  imageSizes = ImageSizes;
  quantityControl = new FormControl(1);
  selectedProduct: Product;
  isDesktop: boolean;

  baseQaId: HabaQaId;
  wishlistBaseQaId: HabaQaId;
  priceTextId: TextQaId;
  basePrice: TextQaId;

  private onDestroy$ = new Subject();

  constructor(
    public router: Router,
    private modalService: ModalService,
    private productService: ProductService,
    private breakpointService: BreakpointService,
    private cdr: ChangeDetectorRef,
    private dlContentClickService: DlContentClickService
  ) {}

  ngOnInit(): void {
    this.initIds();

    this.breakpointService
      .isUp(BREAKPOINT.md)
      .pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe((isDesktop) => {
        this.isDesktop = isDesktop;
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next(undefined);
    this.onDestroy$.complete();
  }

  public openDialog($event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    const modal = this.modalService.open(DynamicModalComponent, {
      windowClass: StorefrontSubdomain['wehrfritz'],
    });
    modal.componentInstance.componentToLoad = ProductStockModalComponent;
    modal.componentInstance.closeButton = true;

    modal.componentInstance.componentData = { product: this.product };
  }

  public addToWishlist(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public addToCart(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.productService
      .get(this.product.code, ProductScope.DETAILS)
      .pipe(
        takeUntil(this.onDestroy$),
        filter((product) => Boolean(product)),
        take(1)
      )
      .subscribe((product) => {
        this.selectedProduct = product;

        // ADD_TO_CART is tracked centrally by CartService.addToCartRegularProduct
        // (invoked through <haba-add-to-cart>), so we only track the configure
        // click here — re-tracking ADD_TO_CART caused duplicate events.
        if (product.configurable) {
          this.dlContentClickService.trackClick('product_advisor_configure', product.code);
        }

        this.cdr.detectChanges();
      });
  }

  public trackProductClick(): void {
    this.dlContentClickService.trackClick('product_advisor_pdp_click', this.product.code, this.product.url);
  }

  public popupClosed(closed: boolean): void {
    if (closed) {
      this.selectedProduct = undefined;
      this.cdr.detectChanges();
    }
  }

  public splitCampaignLabelValue(value: string): string[] {
    return value?.split('|');
  }

  private initIds(): void {
    this.baseQaId = HabaQaId.forPage(HabaQaId.page.productSearchPage);
    this.wishlistBaseQaId = HabaQaId.forPage(HabaQaId.page.wishlist);
    this.basePrice = this.baseQaId.getTextId(HabaQaId.elements.text.priceBase);
    this.priceTextId = this.baseQaId.getTextId(HabaQaId.elements.text.price);
  }
}
